import { useEffect, useState } from 'react';

interface UseDynamicNavbarHeightOptions {
  enabled?: boolean;
  minHeight?: number; // 최소 높이 (px)
}

/**
 * body 스크롤 상태에 따라 사이드바 높이를 동적으로 조정하는 훅
 * @param enabled 훅 활성화 여부
 * @param minHeight 최소 높이 (px, 기본값: 600px)
 */
export const useDynamicNavbarHeight = ({ 
  enabled = true, 
  minHeight = 600 
}: UseDynamicNavbarHeightOptions = {}) => {
  const [navbarHeight, setNavbarHeight] = useState<string>('100vh');
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    const updateNavbarHeight = () => {
      const bodyOverflow = document.body.style.overflow;
      
      // 사이드바의 실제 콘텐츠 높이 측정
      const navbar = document.querySelector('[data-navbar]') as HTMLElement;
      let contentHeight = minHeight;
      
      if (navbar) {
        // 헤더(60px) + 메뉴 콘텐츠 + 푸터(약 60px) + 패딩
        const headerHeight = 60;
        const footerHeight = 60;
        const padding = 40; // 상단 패딩
        
        // 메뉴 컨테이너의 실제 높이 측정
        const menuContainer = navbar.querySelector('.menuContainer') as HTMLElement;
        if (menuContainer) {
          const menuHeight = menuContainer.scrollHeight;
          contentHeight = headerHeight + menuHeight + footerHeight + padding;
        }
      }
      
      if (bodyOverflow === 'auto') {
        // body에 스크롤이 있을 때는 전체 문서 높이와 콘텐츠 높이 중 큰 값 사용
        const documentHeight = Math.max(
          document.body.scrollHeight,
          document.body.offsetHeight,
          document.documentElement.clientHeight,
          document.documentElement.scrollHeight,
          document.documentElement.offsetHeight
        );
        const finalHeight = Math.max(documentHeight, contentHeight, minHeight);
        setNavbarHeight(`${finalHeight}px`);
      } else {
        // body에 스크롤이 없을 때는 뷰포트 높이와 콘텐츠 높이 중 큰 값 사용
        const viewportHeight = window.innerHeight;
        const finalHeight = Math.max(viewportHeight, contentHeight, minHeight);
        setNavbarHeight(`${finalHeight}px`);
      }
    };

    // 초기 설정
    updateNavbarHeight();

    // MutationObserver로 body style 변화 및 메뉴 콘텐츠 변화 감지
    const observer = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          shouldUpdate = true;
        } else if (mutation.type === 'childList') {
          // 메뉴 콘텐츠가 변경된 경우
          shouldUpdate = true;
        }
      });
      
      if (shouldUpdate) {
        // 약간의 지연을 두고 업데이트 (DOM 렌더링 완료 후)
        setTimeout(updateNavbarHeight, 10);
      }
    });

    // body 요소와 사이드바 관찰 시작
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['style']
    });
    
    // 사이드바가 있으면 관찰
    const navbar = document.querySelector('[data-navbar]');
    if (navbar) {
      observer.observe(navbar, {
        childList: true,
        subtree: true
      });
    }

    // 리사이즈 이벤트도 감지
    window.addEventListener('resize', updateNavbarHeight);

    // cleanup
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateNavbarHeight);
    };
  }, [enabled]);

  return {
    navbarHeight,
    // 수동으로 높이를 업데이트할 수 있는 함수
    updateHeight: () => {
      const bodyOverflow = document.body.style.overflow;
      
      if (bodyOverflow === 'auto') {
        const documentHeight = Math.max(
          document.body.scrollHeight,
          document.body.offsetHeight,
          document.documentElement.clientHeight,
          document.documentElement.scrollHeight,
          document.documentElement.offsetHeight
        );
        const finalHeight = Math.max(documentHeight, minHeight);
        setNavbarHeight(`${finalHeight}px`);
      } else {
        const viewportHeight = window.innerHeight;
        const finalHeight = Math.max(viewportHeight, minHeight);
        setNavbarHeight(`${finalHeight}px`);
      }
    }
  };
};
